import { useState, useEffect, useRef } from 'react'
import { Funnel } from '@phosphor-icons/react'
import type { CalendarPlant } from '@/types/calendar'

interface PlantFilterProps {
  plants: CalendarPlant[]
  selectedIds: number[]
  onChange: (ids: number[]) => void
}

export function PlantFilter({ plants, selectedIds, onChange }: PlantFilterProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handleClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    document.addEventListener('keydown', handleKey)
    return () => {
      document.removeEventListener('mousedown', handleClick)
      document.removeEventListener('keydown', handleKey)
    }
  }, [open])

  const toggle = (id: number) => {
    onChange(
      selectedIds.includes(id)
        ? selectedIds.filter(x => x !== id)
        : [...selectedIds, id]
    )
  }

  const label = selectedIds.length === 0
    ? 'Wszystkie rośliny'
    : `${selectedIds.length} ${selectedIds.length === 1 ? 'roślina' : selectedIds.length < 5 ? 'rośliny' : 'roślin'}`

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-xs px-sm py-xs rounded-full font-label text-xs tracking-wide border border-frame/20 text-frame transition-colors hover:border-frame/40"
      >
        <Funnel weight="bold" size={12} />
        {label} ▾
      </button>
      {open && (
        <div className="absolute top-full mt-xs left-0 z-50 bg-paper border border-frame/30 rounded shadow-page min-w-[14rem] max-h-[16rem] overflow-y-auto">
          {selectedIds.length > 0 && (
            <button
              onClick={() => onChange([])}
              className="w-full text-left px-sm py-xs font-label text-xs text-frame/70 hover:bg-paper-dark border-b border-frame/10"
            >
              Wyczyść filtr
            </button>
          )}
          {plants.map(plant => (
            <label
              key={plant.id}
              className="flex items-center gap-xs px-sm py-xs font-label text-xs cursor-pointer hover:bg-paper-dark"
            >
              <input
                type="checkbox"
                checked={selectedIds.includes(plant.id)}
                onChange={() => toggle(plant.id)}
                className="accent-botanical"
              />
              {plant.name}
            </label>
          ))}
        </div>
      )}
    </div>
  )
}
